"use client";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  LogoAvatar,
  Sheet,
  SheetContent,
  SheetTitle,
} from "@pex/ui-web";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  MapPin,
  Video,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { JoinRequestModal } from "@/components/detail/join-request-modal";
import { ContactGroupButton } from "@/components/shared/contact-group-button";
import { formatFilterLabel, formatSchedule } from "@/lib/format";
import { buildMapsHref } from "@/lib/maps";
import type { DiscoveryItem } from "@/lib/queries/discovery";
import type { Group } from "@/lib/queries/groups";
import type { Programme } from "@/lib/queries/programmes";

const IOS_REGEX = /iPad|iPhone|iPod/;

interface Props {
  items: DiscoveryItem[];
  onClose: () => void;
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      aria-label="Close"
      className="cursor-pointer rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      onClick={onClose}
      type="button"
    >
      <X className="h-3.5 w-3.5" />
    </button>
  );
}

function PaginationBar({
  index,
  total,
  onPrev,
  onNext,
}: {
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex items-center justify-between px-4">
      <button
        aria-label="Previous"
        className="cursor-pointer rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
        disabled={index === 0}
        onClick={onPrev}
        type="button"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      <span className="text-muted-foreground text-xs">
        {index + 1} of {total}
      </span>
      <button
        aria-label="Next"
        className="cursor-pointer rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
        disabled={index === total - 1}
        onClick={onNext}
        type="button"
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function buildGroupAddress(g: Group): string {
  return [g.street_address, g.suburb, g.city, g.postal_code, g.country]
    .filter(Boolean)
    .join(", ");
}

function buildProgrammeAddress(p: Programme): string {
  return [p.street_address, p.suburb, p.city, p.postal_code]
    .filter(Boolean)
    .join(", ");
}

function GroupCardBody({
  group,
  pagination,
}: {
  group: Group;
  pagination?: React.ReactNode;
}) {
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    setIsIOS(IOS_REGEX.test(navigator.userAgent));
  }, []);

  const schedule = formatSchedule(
    group.meeting_day,
    group.meeting_start_time,
    group.timezone
  );
  const location = [group.city, group.country].filter(Boolean).join(", ");
  const fullAddress = buildGroupAddress(group);
  const isOnline = group.format === "online";
  // name is non-nullable in the schema; ?? is a defensive runtime fallback
  const displayName = group.name ?? group.organization?.name;

  const mapsHref = buildMapsHref(
    isIOS,
    fullAddress,
    group.latitude,
    group.longitude,
    displayName
  );

  return (
    <>
      <CardHeader className="border-b">
        <div className="flex items-start gap-3 pr-7">
          <LogoAvatar
            alt={`${group.organization?.name ?? displayName} logo`}
            name={displayName}
            size={44}
            src={group.organization?.logo_url}
          />
          <div className="min-w-0 flex-1 space-y-0.5">
            <p className="truncate font-semibold text-[15px] leading-snug">
              {displayName}
            </p>
            {location && (
              <p className="flex items-center gap-1 truncate text-muted-foreground text-xs">
                <MapPin className="h-3 w-3 shrink-0" />
                {location}
              </p>
            )}
            {schedule && (
              <p className="flex items-center gap-1 truncate text-muted-foreground text-xs">
                <Clock className="h-3 w-3 shrink-0" />
                {schedule}
              </p>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-2.5">
        {(group.specialisation || group.access) && (
          <div className="flex flex-wrap gap-1.5">
            {group.specialisation && (
              <Badge variant="secondary">
                {formatFilterLabel(group.specialisation)}
              </Badge>
            )}
            {group.access && (
              <Badge variant="outline">
                {group.access === "open" ? "Open" : "Invite Only"}
              </Badge>
            )}
          </div>
        )}
        {!isOnline && fullAddress && (
          <p className="text-muted-foreground text-xs leading-relaxed">
            {fullAddress}
          </p>
        )}
        {isOnline && group.online_meeting_url && (
          <a
            className="text-primary text-xs transition-colors hover:underline"
            href={group.online_meeting_url}
            rel="noopener"
            target="_blank"
          >
            Join online meeting ↗
          </a>
        )}
      </CardContent>

      {((!isOnline && !!fullAddress) || group.has_contact_email) && (
        <CardFooter className="flex-col items-stretch gap-2 border-t">
          {!isOnline && fullAddress && (
            <Button asChild size="sm">
              <a href={mapsHref} rel="noopener" target="_blank">
                <MapPin className="h-4 w-4" />
                Open in Maps
              </a>
            </Button>
          )}
          {group.has_contact_email && (
            <ContactGroupButton groupId={group.id} size="sm" />
          )}
        </CardFooter>
      )}

      {pagination}
    </>
  );
}

function ProgrammeCardBody({
  programme,
  pagination,
}: {
  programme: Programme;
  pagination?: React.ReactNode;
}) {
  const [joinOpen, setJoinOpen] = useState(false);

  const { definition } = programme;
  const schedule = formatSchedule(
    programme.meeting_day,
    programme.meeting_start_time
  );
  const fullAddress = buildProgrammeAddress(programme);
  const isOnline = programme.format === "online";
  const isOpen = programme.access === "open";
  const displayName = definition?.name ?? "Programme";
  const location = [programme.city, programme.suburb]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <CardHeader className="border-b">
        <div className="flex items-start gap-3 pr-7">
          <LogoAvatar
            alt={`${displayName} logo`}
            name={displayName}
            size={44}
            src={definition?.logo_letter_url}
          />
          <div className="min-w-0 flex-1 space-y-0.5">
            <p className="truncate font-medium text-[11px] text-muted-foreground uppercase tracking-widest">
              {displayName}
            </p>
            <p className="truncate font-semibold text-[15px] leading-snug">
              {programme.organization?.name ?? displayName}
            </p>
            {location && (
              <p className="flex items-center gap-1 truncate text-muted-foreground text-xs">
                <MapPin className="h-3 w-3 shrink-0" />
                {location}
              </p>
            )}
            {schedule && (
              <p className="flex items-center gap-1 truncate text-muted-foreground text-xs">
                <Clock className="h-3 w-3 shrink-0" />
                {schedule}
              </p>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-2.5">
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="secondary">{isOpen ? "Open" : "Invite Only"}</Badge>
        </div>
        {!isOnline && fullAddress && (
          <p className="text-muted-foreground text-xs leading-relaxed">
            {fullAddress}
          </p>
        )}
        {!isOpen && (
          <p className="text-muted-foreground text-xs">
            Only open-access programmes accept join requests.
          </p>
        )}
      </CardContent>

      {(isOpen ||
        !!definition?.website_url ||
        (isOnline && !!programme.online_meeting_url)) && (
        <CardFooter className="flex-col items-stretch gap-2 border-t">
          {isOpen && (
            <Button onClick={() => setJoinOpen(true)} size="sm">
              Request to Join
            </Button>
          )}
          {definition?.website_url && (
            <Button asChild size="sm" variant="outline">
              <a href={definition.website_url} rel="noopener" target="_blank">
                Learn More
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          )}
          {isOnline && programme.online_meeting_url && (
            <Button asChild size="sm" variant="outline">
              <a
                href={programme.online_meeting_url}
                rel="noopener"
                target="_blank"
              >
                <Video className="h-4 w-4" />
                Join Meeting
              </a>
            </Button>
          )}
        </CardFooter>
      )}

      {pagination}

      <JoinRequestModal
        onOpenChange={setJoinOpen}
        open={joinOpen}
        programmeLocationId={programme.id}
      />
    </>
  );
}

/**
 * Shared inner content: owns the pagination/carousel state and renders the
 * correct body. Rendered inside the desktop Card and the mobile Sheet alike —
 * both supply the `group/card` + `data-size="sm"` context the Card sub-parts
 * (header/content/footer) read for their padding and spacing.
 */
function PinDetailContent({ items }: { items: DiscoveryItem[] }) {
  const [index, setIndex] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: items is a prop — resets carousel when a new pin is selected
  useEffect(() => {
    setIndex(0);
  }, [items]);

  const item = items[index] ?? items[0];
  if (!item) {
    return null;
  }
  const total = items.length;

  const pagination =
    total > 1 ? (
      <PaginationBar
        index={index}
        onNext={() => setIndex((i) => Math.min(total - 1, i + 1))}
        onPrev={() => setIndex((i) => Math.max(0, i - 1))}
        total={total}
      />
    ) : null;

  if (item.kind === "group") {
    return <GroupCardBody group={item.data} pagination={pagination} />;
  }
  return <ProgrammeCardBody pagination={pagination} programme={item.data} />;
}

/**
 * Desktop variant — the floating card portaled into a Mapbox popup. Unchanged
 * behaviour: a 320px card with its own close button.
 */
export function PinDetailCard({ items, onClose }: Props) {
  if (items.length === 0) {
    return null;
  }
  return (
    <Card className="relative w-80" size="sm">
      <div className="absolute top-3 right-3 z-10">
        <CloseButton onClose={onClose} />
      </div>
      <PinDetailContent items={items} />
    </Card>
  );
}

/**
 * Mobile variant — the same content in a bottom sheet. The Sheet supplies its
 * own close button, so none is added here. The wrapping div recreates the
 * `group/card` + `data-size="sm"` context (which the Card normally provides)
 * so the body's padding and spacing match the desktop card.
 */
export function PinDetailSheet({
  items,
  onOpenChange,
  open,
}: {
  items: DiscoveryItem[];
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) {
  if (items.length === 0) {
    return null;
  }
  return (
    <Sheet onOpenChange={onOpenChange} open={open}>
      <SheetContent
        aria-describedby={undefined}
        className="max-h-[90vh] rounded-t-2xl"
        side="bottom"
      >
        <SheetTitle className="sr-only">Location details</SheetTitle>
        <div
          className="group/card flex flex-col gap-4 overflow-y-auto py-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
          data-size="sm"
        >
          <PinDetailContent items={items} />
        </div>
      </SheetContent>
    </Sheet>
  );
}
